/**
 * Internal Controls — ESRS 2 GOV-5
 *
 * Register of internal controls over the sustainability reporting process,
 * mapped to COSO 2013 components, with periodic tests of control. The GOV-5
 * summary counts only ACTIVE controls; the latest test per control wins.
 * Controls are retired, never deleted — history is preserved. Entity-scoped
 * only (no reporting_period parameter; tests carry their own period string).
 *
 * Backend contract (backend/routes/internal_control_routes.py):
 *   GET  /api/v1/internal-controls/controls?entity_id=        → { controls: [...] }
 *   GET  /api/v1/internal-controls/gov5-summary?entity_id=    → { controls, testing, requirement_coverage, open_items }
 *   POST /api/v1/internal-controls/controls                   → control
 *   POST /api/v1/internal-controls/controls/{id}/retire       → retired control
 *   POST /api/v1/internal-controls/controls/{id}/tests        → test record
 */
import { useCallback, useEffect, useState } from "react";
import {
  ShieldCheck, Shield, Search, Wrench, CircleAlert, XCircle, AlertTriangle,
  Star, Archive, Plus, X, Gauge, ListChecks, CheckCircle2, ClipboardCheck, Tags,
} from "lucide-react";
import {
  Page, PageHeader, Card, Overline, Metric, Badge, Data, Btn, DataTable,
  EmptyState, ErrorBanner, Skeleton, field, Label, apiFetch, fmt, C, SANS,
} from "../../components/esg/EsgKit";
import { useEntities } from "../../hooks/useEntities";

const COSO_OPTIONS = [
  { value: "control_environment", label: "Control environment", short: "CE" },
  { value: "risk_assessment", label: "Risk assessment", short: "RA" },
  { value: "control_activities", label: "Control activities", short: "CA" },
  { value: "information_communication", label: "Information & communication", short: "IC" },
  { value: "monitoring", label: "Monitoring", short: "MO" },
];
const cosoLabel = (v) => COSO_OPTIONS.find((o) => o.value === v)?.label || v || "—";

const TYPE_OPTIONS = [
  { value: "preventive", label: "Preventive" },
  { value: "detective", label: "Detective" },
  { value: "corrective", label: "Corrective" },
];

const FREQUENCY_OPTIONS = [
  { value: "continuous", label: "Continuous" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annual", label: "Annual" },
];

const RESULT_OPTIONS = [
  { value: "effective", label: "Effective" },
  { value: "partially_effective", label: "Partially effective" },
  { value: "ineffective", label: "Ineffective" },
];

function typeBadge(t) {
  switch (t) {
    case "preventive":
      return <Badge c={C.locked} Icon={Shield} label="Preventive" />;
    case "detective":
      return <Badge c={C.accent} Icon={Search} label="Detective" />;
    case "corrective":
      return <Badge c={C.primary} Icon={Wrench} label="Corrective" />;
    default:
      return <Badge c={C.neutral} Icon={Shield} label={t || "—"} />;
  }
}

function statusBadge(s) {
  return s === "retired"
    ? <Badge c={C.neutral} Icon={Archive} label="Retired" />
    : <Badge c={C.primary} Icon={CheckCircle2} label="Active" />;
}

function issueBadge(issue) {
  switch (issue) {
    case "never_tested":
      return <Badge c={C.accent} Icon={CircleAlert} label="Never tested" />;
    case "latest_test_ineffective":
      return <Badge c={C.destructive} Icon={XCircle} label="Ineffective" />;
    case "latest_test_partially_effective":
      return <Badge c={C.accent} Icon={AlertTriangle} label="Partially effective" />;
    default:
      return <Badge c={C.neutral} Icon={CircleAlert} label={issue || "—"} />;
  }
}

const EMPTY_FORM = {
  name: "",
  description: "",
  coso_component: "control_environment",
  control_type: "preventive",
  frequency: "quarterly",
  owner: "",
  esrs_requirement_codes: "",
  key_control: false,
};

const EMPTY_TEST = { period: "", result: "effective", tested_by: "", notes: "" };

export default function InternalControls() {
  const { entities, entityId, setEntityId } = useEntities("internal_controls_entity_id");

  const [summary, setSummary] = useState(null);
  const [controls, setControls] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [testTarget, setTestTarget] = useState(null);
  const [testForm, setTestForm] = useState(EMPTY_TEST);
  const [testSaving, setTestSaving] = useState(false);

  const [retiringId, setRetiringId] = useState(null);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setError("");
    const q = `entity_id=${encodeURIComponent(entityId)}`;
    try {
      const [sum, ctrls] = await Promise.all([
        apiFetch(`/api/v1/internal-controls/gov5-summary?${q}`),
        apiFetch(`/api/v1/internal-controls/controls?${q}`),
      ]);
      setSummary(sum || null);
      setControls(ctrls?.controls || []);
    } catch (e) {
      setError(e.message || "Failed to load internal controls");
      setSummary(null);
      setControls([]);
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTestField = (k, v) => setTestForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!entityId) return;
    setSaving(true);
    setError("");
    try {
      await apiFetch("/api/v1/internal-controls/controls", {
        method: "POST",
        body: {
          entity_id: entityId,
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          coso_component: form.coso_component,
          control_type: form.control_type,
          frequency: form.frequency,
          owner: form.owner.trim() || undefined,
          esrs_requirement_codes: form.esrs_requirement_codes
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
          key_control: form.key_control,
        },
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err.message || "Failed to create control");
    } finally {
      setSaving(false);
    }
  };

  const submitTest = async (e) => {
    e.preventDefault();
    if (!testTarget) return;
    setTestSaving(true);
    setError("");
    try {
      await apiFetch(`/api/v1/internal-controls/controls/${testTarget.control_id}/tests`, {
        method: "POST",
        body: {
          period: testForm.period.trim(),
          result: testForm.result,
          tested_by: testForm.tested_by.trim(),
          notes: testForm.notes.trim() || undefined,
        },
      });
      setTestTarget(null);
      setTestForm(EMPTY_TEST);
      await load();
    } catch (err) {
      setError(err.message || "Failed to record test of control");
    } finally {
      setTestSaving(false);
    }
  };

  const retire = async (controlId) => {
    setRetiringId(controlId);
    setError("");
    try {
      await apiFetch(`/api/v1/internal-controls/controls/${controlId}/retire`, {
        method: "POST",
      });
      if (testTarget?.control_id === controlId) {
        setTestTarget(null);
        setTestForm(EMPTY_TEST);
      }
      await load();
    } catch (err) {
      setError(err.message || "Failed to retire control");
    } finally {
      setRetiringId(null);
    }
  };

  const header = (
    <PageHeader
      overline="ESRS 2 GOV-5 · Internal Controls"
      OverlineIcon={ShieldCheck}
      title="Internal Controls"
      subtitle="Control register and tests of control over the sustainability reporting process — GOV-5 risk management and internal controls, mapped to the five COSO 2013 components."
      entities={entities}
      entityId={entityId}
      onEntityChange={setEntityId}
      onRefresh={load}
      loading={loading}
    />
  );

  if (!entityId) {
    return (
      <Page>
        {header}
        <Card>
          <EmptyState
            Icon={ShieldCheck}
            title="No reporting entity selected"
            body="Choose a reporting entity to view its GOV-5 control register, tests of control and ESRS requirement coverage."
          />
        </Card>
      </Page>
    );
  }

  const sc = summary?.controls || {};
  const st = summary?.testing || {};
  const coverage = summary?.requirement_coverage || {};
  const openItems = summary?.open_items || [];
  const byCoso = sc.by_coso_component || {};

  const kpis = [
    { label: "Active controls", value: fmt(sc.total, 0) },
    { label: "Key controls", value: fmt(sc.key_controls, 0), color: C.locked },
    { label: "Tested", value: fmt(st.controls_tested, 0) },
    {
      label: "Never tested",
      value: fmt(st.controls_never_tested, 0),
      color: (st.controls_never_tested || 0) > 0 ? C.accent : C.ink,
    },
    { label: "Latest effective", value: fmt(st.latest_effective, 0), color: C.primary },
    { label: "Latest partially effective", value: fmt(st.latest_partially_effective, 0), color: C.accent },
    { label: "Latest ineffective", value: fmt(st.latest_ineffective, 0), color: C.destructive },
  ];

  const coverageEntries = Object.entries(coverage).sort(([a], [b]) => a.localeCompare(b));

  const openColumns = [
    {
      key: "name",
      label: "Control",
      render: (r) => <Data>{r.name || r.control_id || "—"}</Data>,
    },
    { key: "issue", label: "Issue", render: (r) => issueBadge(r.issue) },
    {
      key: "period",
      label: "Period",
      render: (r) => <Data color={C.body}>{r.period || "—"}</Data>,
    },
    {
      key: "key_control",
      label: "Key",
      render: (r) =>
        r.key_control ? <Badge c={C.locked} Icon={Star} label="Key control" /> : <Data color={C.muted}>—</Data>,
    },
  ];

  const registerColumns = [
    {
      key: "name",
      label: "Control",
      render: (r) => (
        <div>
          <Data>{r.name || "—"}</Data>
          {r.description && (
            <div
              style={{
                fontFamily: SANS, fontSize: 11, color: C.muted, marginTop: 2,
                maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}
              title={r.description}
            >
              {r.description}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "coso_component",
      label: "COSO component",
      render: (r) => <span style={{ fontFamily: SANS, fontSize: 12, color: C.body }}>{cosoLabel(r.coso_component)}</span>,
    },
    { key: "control_type", label: "Type", render: (r) => typeBadge(r.control_type) },
    {
      key: "frequency",
      label: "Frequency",
      render: (r) => <Data color={C.body}>{r.frequency || "—"}</Data>,
    },
    {
      key: "owner",
      label: "Owner",
      render: (r) => <span style={{ fontFamily: SANS, fontSize: 12, color: C.body }}>{r.owner || "—"}</span>,
    },
    {
      key: "esrs_requirement_codes",
      label: "ESRS codes",
      render: (r) => (
        <Data color={C.body}>
          {(r.esrs_requirement_codes || []).length ? r.esrs_requirement_codes.join(", ") : "—"}
        </Data>
      ),
    },
    {
      key: "key_control",
      label: "Key",
      render: (r) =>
        r.key_control ? <Badge c={C.locked} Icon={Star} label="Key control" /> : <Data color={C.muted}>—</Data>,
    },
    { key: "status", label: "Status", render: (r) => statusBadge(r.status) },
    {
      key: "actions",
      label: "Actions",
      render: (r) =>
        r.status === "active" ? (
          <div style={{ display: "flex", gap: 6 }}>
            <Btn
              variant="secondary"
              Icon={ClipboardCheck}
              onClick={() => { setTestTarget(r); setTestForm(EMPTY_TEST); }}
              title="Record a test of control"
            >
              Record test
            </Btn>
            <Btn
              variant="destructive"
              Icon={Archive}
              onClick={() => retire(r.control_id)}
              disabled={retiringId === r.control_id}
              title="Retire this control (history is preserved)"
            >
              {retiringId === r.control_id ? "Retiring…" : "Retire"}
            </Btn>
          </div>
        ) : (
          <Data color={C.muted}>—</Data>
        ),
    },
  ];

  return (
    <Page>
      {header}
      <ErrorBanner message={error} onDismiss={() => setError("")} />

      {loading && !summary ? (
        <div style={{ display: "grid", gap: 16 }}>
          <Skeleton h={130} />
          <Skeleton h={160} />
          <Skeleton h={240} />
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          {/* GOV-5 KPI strip */}
          <Card>
            <Overline Icon={Gauge}>GOV-5 summary · {summary?.esrs_reference || "ESRS 2 GOV-5"}</Overline>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: 20,
              }}
            >
              {kpis.map((k) => (
                <Metric key={k.label} label={k.label} value={k.value} color={k.color} />
              ))}
            </div>
            <div
              style={{
                marginTop: 16,
                display: "flex",
                gap: 20,
                flexWrap: "wrap",
                fontFamily: SANS,
                fontSize: 12,
                color: C.body,
              }}
            >
              <span>COSO components</span>
              {COSO_OPTIONS.map((o, i) => (
                <span key={o.value} title={o.label}>
                  <Data color={C.body}>{o.short}</Data>{" "}
                  <Data color={C.ink}>{fmt(byCoso[o.value], 0)}</Data>
                  {i < COSO_OPTIONS.length - 1 && <span style={{ color: C.muted }}> ·</span>}
                </span>
              ))}
            </div>
          </Card>

          {/* Open items */}
          <Card>
            <Overline Icon={AlertTriangle} color={C.body}>
              Open items
            </Overline>
            {openItems.length === 0 ? (
              (sc.total || 0) === 0 ? (
                <EmptyState
                  Icon={AlertTriangle}
                  title="No controls registered"
                  body="GOV-5 readiness cannot be assessed until controls over sustainability reporting are registered and tested. Add the first control above."
                />
              ) : (
                <EmptyState
                  Icon={CheckCircle2}
                  title="No open control items"
                  body="Every active control has been tested and the latest test of each control was effective. GOV-5 is assurance-ready for this entity."
                />
              )
            ) : (
              <DataTable
                columns={openColumns}
                rows={openItems}
                keyField="control_id"
                emptyLabel="No open control items"
              />
            )}
          </Card>

          {/* Requirement coverage */}
          <Card>
            <Overline Icon={Tags} color={C.body}>
              Requirement coverage
            </Overline>
            {coverageEntries.length === 0 ? (
              <EmptyState
                Icon={Tags}
                title="No ESRS requirements covered"
                body="Tag controls with esrs_requirement_codes (e.g. E1-6, E5-5) to map which disclosure requirements each control addresses."
              />
            ) : (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {coverageEntries.map(([code, count]) => (
                  <span
                    key={code}
                    title={`${count} control${count === 1 ? "" : "s"} cover ${code}`}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 8,
                      padding: "5px 10px", borderRadius: 6,
                      border: `1px solid ${C.border}`, background: C.bg,
                    }}
                  >
                    <Data>{code}</Data>
                    <Data color={C.primary}>{fmt(count, 0)}</Data>
                  </span>
                ))}
              </div>
            )}
          </Card>

          {/* Test of control form */}
          {testTarget && (
            <Card accent={C.locked}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <Overline Icon={ClipboardCheck} style={{ marginBottom: 0 }}>
                  Record test of control
                </Overline>
                <Btn variant="ghost" Icon={X} onClick={() => { setTestTarget(null); setTestForm(EMPTY_TEST); }}>
                  Cancel
                </Btn>
              </div>
              <div style={{ marginTop: 12, fontFamily: SANS, fontSize: 13, color: C.body }}>
                Testing <Data>{testTarget.name}</Data>
              </div>
              <form onSubmit={submitTest} style={{ marginTop: 14 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: 14,
                  }}
                >
                  <div>
                    <Label>Period *</Label>
                    <input
                      value={testForm.period}
                      onChange={(e) => setTestField("period", e.target.value)}
                      required
                      placeholder="2026-Q2"
                      style={field}
                    />
                  </div>
                  <div>
                    <Label>Result *</Label>
                    <select
                      value={testForm.result}
                      onChange={(e) => setTestField("result", e.target.value)}
                      required
                      style={field}
                    >
                      {RESULT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Tested by *</Label>
                    <input
                      value={testForm.tested_by}
                      onChange={(e) => setTestField("tested_by", e.target.value)}
                      required
                      placeholder="Internal audit"
                      style={field}
                    />
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <input
                      value={testForm.notes}
                      onChange={(e) => setTestField("notes", e.target.value)}
                      placeholder="Sample of 25 items, no exceptions"
                      style={field}
                    />
                  </div>
                </div>
                <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                  <Btn
                    type="submit"
                    Icon={ClipboardCheck}
                    disabled={testSaving || !testForm.period.trim() || !testForm.tested_by.trim()}
                  >
                    {testSaving ? "Saving…" : "Record test"}
                  </Btn>
                  <Btn variant="secondary" onClick={() => { setTestTarget(null); setTestForm(EMPTY_TEST); }}>
                    Cancel
                  </Btn>
                </div>
              </form>
            </Card>
          )}

          {/* Add control */}
          <Card>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <Overline Icon={Plus} color={C.body} style={{ marginBottom: 0 }}>
                Add control
              </Overline>
              <Btn
                variant={showForm ? "ghost" : "secondary"}
                Icon={showForm ? X : Plus}
                onClick={() => setShowForm((s) => !s)}
              >
                {showForm ? "Cancel" : "Add control"}
              </Btn>
            </div>

            {showForm && (
              <form onSubmit={submit} style={{ marginTop: 16 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: 14,
                  }}
                >
                  <div>
                    <Label>Name *</Label>
                    <input
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                      required
                      placeholder="Scope 1 activity-data reconciliation"
                      style={field}
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <input
                      value={form.description}
                      onChange={(e) => setField("description", e.target.value)}
                      placeholder="Monthly reconciliation of meter data to ERP"
                      style={field}
                    />
                  </div>
                  <div>
                    <Label>COSO component *</Label>
                    <select
                      value={form.coso_component}
                      onChange={(e) => setField("coso_component", e.target.value)}
                      required
                      style={field}
                    >
                      {COSO_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Control type *</Label>
                    <select
                      value={form.control_type}
                      onChange={(e) => setField("control_type", e.target.value)}
                      required
                      style={field}
                    >
                      {TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Frequency *</Label>
                    <select
                      value={form.frequency}
                      onChange={(e) => setField("frequency", e.target.value)}
                      required
                      style={field}
                    >
                      {FREQUENCY_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Owner</Label>
                    <input
                      value={form.owner}
                      onChange={(e) => setField("owner", e.target.value)}
                      placeholder="Group ESG controller"
                      style={field}
                    />
                  </div>
                  <div>
                    <Label>ESRS requirement codes (comma-separated)</Label>
                    <input
                      value={form.esrs_requirement_codes}
                      onChange={(e) => setField("esrs_requirement_codes", e.target.value)}
                      placeholder="E1-6, E5-5"
                      style={field}
                    />
                  </div>
                  <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 8 }}>
                    <label
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 8,
                        fontFamily: SANS, fontSize: 12, color: C.body, cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={form.key_control}
                        onChange={(e) => setField("key_control", e.target.checked)}
                        style={{ accentColor: C.locked, width: 14, height: 14 }}
                      />
                      Key control (relied on for assurance)
                    </label>
                  </div>
                </div>
                <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                  <Btn type="submit" Icon={Plus} disabled={saving || !form.name.trim()}>
                    {saving ? "Saving…" : "Create control"}
                  </Btn>
                  <Btn variant="secondary" onClick={() => { setForm(EMPTY_FORM); setShowForm(false); }}>
                    Reset
                  </Btn>
                </div>
              </form>
            )}
          </Card>

          {/* Control register */}
          <Card>
            <Overline Icon={ListChecks} color={C.body}>
              Control register
            </Overline>
            {controls.length === 0 ? (
              <EmptyState
                Icon={ShieldCheck}
                title="No controls registered"
                body="Register the internal controls that govern this entity's sustainability reporting process — GOV-5 requires disclosing how risk management and internal controls operate over reported data."
              />
            ) : (
              <DataTable
                columns={registerColumns}
                rows={controls}
                keyField="control_id"
                emptyLabel="No controls registered"
              />
            )}
          </Card>
        </div>
      )}
    </Page>
  );
}
